import apiClient from '../../../shared/api/client';
import { OfferDetail } from '../types/offer-detail.types';
import purchaseService from '../../purchases/services/purchaseService';

export interface MessageResponse {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  createdAt: string;
  readAt?: string | null;
}

class OfferDetailService {
  private readonly baseUrl = '/offers';
  private readonly favoritesUrl = '/favorites';

  async getOfferById(id: string): Promise<OfferDetail> {
    return await apiClient.get<OfferDetail>(`${this.baseUrl}/${id}`);
  }

  async toggleFavorite(offerId: string, isFavorite: boolean): Promise<void> {
    if (isFavorite) {
      // Remove from favorites
      await apiClient.delete(`${this.favoritesUrl}/${offerId}`);
    } else {
      // Add to favorites
      await apiClient.post(`${this.favoritesUrl}/${offerId}`);
    }
  }

  async getUserFavorites(): Promise<string[]> {
    return await apiClient.get<string[]>(this.favoritesUrl);
  }

  /**
   * Register buyer interest in an offer (creates a purchase record; offer stays PUBLISHED)
   * @param offerId - ID of the offer
   */
  async interestOffer(offerId: string): Promise<void> {
    await purchaseService.interestOffer(offerId);
  }

  /**
   * Close an offer (owner action — marks offer as CLOSED)
   * @param offerId - ID of the offer to close
   * @param closureReason - Reason for closing (VENTE_CONCLUE or AUTRE)
   */
  async closeOffer(offerId: string, closureReason: 'VENTE_CONCLUE' | 'AUTRE'): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${offerId}/close`, { closureReason });
  }

  async sendMessage(offerId: string, body: string): Promise<MessageResponse> {
    return await apiClient.post<MessageResponse>('/messages', {
      offerId,
      body,
    });
  }

  /**
   * Report an offer to the administrators
   * @param offerId - ID of the offer being reported
   * @param reason - one of predefined reasons
   * @param details - optional free-text details when reason is 'OTHER'
   */
  async reportOffer(
    offerId: string,
    reason: 'INCORRECT' | 'OFFENSIVE' | 'OTHER',
    details?: string
  ): Promise<void> {
    await apiClient.post('/reports', {
      offerId,
      reason,
      details,
    });
  }

  async downloadDocument(documentId: string): Promise<Blob> {
    return await apiClient.get<Blob>(`${this.baseUrl}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
  }
}

export default new OfferDetailService();
